import type { GmvBreakdownLens } from "@unbox/api";
import { pluralize } from "@/utils/text";

export const LENS_TABS: Array<{ id: GmvBreakdownLens; label: string }> = [
  { id: "product", label: "By product" },
  { id: "cohort", label: "By cohort" },
  { id: "creator", label: "By creator" },
];

export const REMAINDER_COUNTS: Record<GmvBreakdownLens, string> = {
  product: "Every video whose product card points at something other than the products named here.",
  cohort: "Videos from creators in any cohort other than the ones named here.",
  creator: "Videos from every creator other than the ones named here.",
};

export const REMAINDER_CHIP = "Remainder";

export const MEMBERS_NOTE = "What each one added to, or took off, this column.";

export const CHECK_ALL_ACTION: Record<GmvBreakdownLens, string> = {
  product: "Check products",
  cohort: "Check cohorts",
  creator: "Check creators",
};

export const REMAINDER_NAME: Record<GmvBreakdownLens, string> = {
  product: "All other products",
  cohort: "All other cohorts",
  creator: "All other creators",
};

export const OPEN_ACTION: Record<GmvBreakdownLens, string> = {
  product: "Open this product",
  cohort: "Open this cohort",
  creator: "Open this creator",
};

export const ROLE_LABEL: Record<GmvBreakdownLens, string> = {
  product: "Product",
  cohort: "Cohort",
  creator: "Creator",
};

export const REMAINDER_ROLE: Record<GmvBreakdownLens, string> = {
  product: "The rest of the catalogue",
  cohort: "The rest of the roster",
  creator: "The rest of the roster",
};

export const ASK_ACTION: Record<GmvBreakdownLens, string> = {
  product: "Ask Unbox about product",
  cohort: "Ask Unbox about cohort",
  creator: "Ask Unbox about creator",
};

// The "and N new videos..." clause in the lifetime summary text — only creator/cohort show
// it (product's screenshot has no equivalent), and the wording differs on purpose per lens.
export const NEW_VIDEOS_PHRASE: Record<GmvBreakdownLens, (count: number) => string | null> = {
  product: () => null,
  creator: (count) => `and created ${pluralize(count, "new video", "new videos")}`,
  cohort: (count) => `and ${pluralize(count, "new video", "new videos")} were created`,
};

export const BEST_VIDEO_PHRASE: Record<GmvBreakdownLens, { before: string; after: string }> = {
  product: { before: "Best video featuring this product made", after: " GMV" },
  creator: { before: "Best performing video made", after: "" },
  cohort: { before: "Best performing video made", after: "" },
};

export const askAboutQuestion = (name: string, role: string) => `${name} — ${role.toLowerCase()}. What should I know?`;

export const headline = (delta: number, phrase: string, formatGmv: (value: number) => string) =>
  `Video GMV ${delta < 0 ? "fell" : "grew"} by ${formatGmv(Math.abs(delta))} in the ${phrase}`;

export const subline = (current: number, prior: number, phrase: string, formatGmv: (value: number) => string): string =>
  `From ${formatGmv(prior)} to ${formatGmv(current)} in video-attributed GMV over the ${phrase}, against the previous ${phrase.replace(/^last /, "")}.`;
